import { supabase } from '@/lib/supabase'

export interface SignUpData {
  email: string
  password: string
}

export interface SignInData {
  email: string
  password: string
}

export const authService = {
  /**
   * Sign up new user with email and password
   * Creates auth user and profile record via database trigger
   */
  async signUp({ email, password }: SignUpData) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) throw error
    if (!data.user) throw new Error('User creation failed')

    return { user: data.user, session: data.session }
  },

  /**
   * Sign in with email and password
   */
  async signIn({ email, password }: SignInData) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error
    return { user: data.user, session: data.session }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    // scope: 'local' ends only this browser's session. The default is 'global',
    // which deletes every session the user has — signing out here would also
    // kill the Chrome extension and desktop installer, which then fail with
    // refresh_token_not_found on their next refresh.
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    if (error) throw error
  },

  /**
   * Get current session
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession()
    if (error) throw error
    return data.session
  },

  async resetPasswordForEmail(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  },

  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
  },
}
